import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, featuredArticles } from '@/mocks/home';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 pb-20 text-center">
          <h1 className="text-3xl font-heading font-bold text-foreground-950 mb-4">分类未找到</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm">返回首页</Link>
        </main>
        <Footer />
      </div>
    );
  }

  const categoryArticles = featuredArticles.filter(
    (a) => a.categorySlug === slug
  );

  const otherArticles = featuredArticles.filter(
    (a) => a.categorySlug !== slug
  );

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.91chigua.com' },
      { '@type': 'ListItem', position: 2, name: category.name, item: `https://www.91chigua.com/category/${slug}` },
    ],
  };

  return (
    <>
      <title>{category.name} - 91吃瓜网 | {category.description}</title>
      <meta name="description" content={`91吃瓜网${category.name}频道，${category.description}。实时追踪最新${category.name}资讯，吃瓜群众的必刷入口。`} />
      <meta name="keywords" content={`91吃瓜网,${category.name},91吃瓜爆料,91吃瓜视频,吃瓜群众,娱乐八卦`} />
      <link rel="canonical" href={`https://www.91chigua.com/category/${slug}`} />
      <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <section className="relative w-full h-[320px] md:h-[420px] overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=Dramatic%20cinematic%20entertainment%20scene%2C%20warm%20amber%20stage%20lighting%2C%20mysterious%20theater%20atmosphere%2C%20spotlight%20on%20empty%20stage%2C%20golden%20warm%20tones%2C%20high%20contrast%2C%20no%20visible%20people%2C%20editorial%20abstract%20style&width=1800&height=800&seq=category-hero-generic&orientation=landscape"
                alt={category.name}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/65"></div>
            </div>

            <div className="relative z-10 h-full flex flex-col justify-center px-4 md:px-6">
              <div className="max-w-3xl">
                <nav className="flex items-center gap-2 text-sm mb-4 text-background-50/60" aria-label="Breadcrumb">
                  <Link to="/" className="hover:text-background-50 transition-colors">首页</Link>
                  <i className="ri-arrow-right-s-line text-xs"></i>
                  <span className="text-background-50">{category.name}</span>
                </nav>
                <h1 className="text-3xl md:text-5xl font-heading font-bold text-background-50 leading-tight mb-3">
                  {category.name}
                </h1>
                <p className="text-sm md:text-base text-background-50/70 max-w-lg">
                  {category.description}
                </p>
              </div>
            </div>
          </section>

          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="mb-10">
                <span className="text-xs text-secondary-500 uppercase tracking-wider">{category.name}</span>
                <h2 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950 leading-tight mt-2">
                  最新
                  <span className="inline-block mx-2 w-12 h-12 md:w-16 md:h-16 rounded-full overflow-hidden align-middle">
                    <img
                      src="https://readdy.ai/api/search-image?query=Warm%20dramatic%20spotlight%20circle%2C%20golden%20amber%20glow%2C%20entertainment%20industry%20abstract%2C%20cinematic%20texture%2C%20soft%20focus%20bokeh%2C%20theater%20light%20effect&width=80&height=80&seq=cat-intro-img-01&orientation=squarish"
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </span>
                  {category.name}
                  <br />
                  爆料追踪
                </h2>
                <p className="mt-3 text-sm text-foreground-500 max-w-xl">
                  第一时间追踪{category.name}最新动态，不错过任何一个大瓜
                </p>

                <div className="flex items-center gap-3 mt-6">
                  <Link
                    to={`/category/${slug}`}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-foreground-950 text-background-50 text-sm font-medium hover:bg-foreground-800 transition-colors duration-200 whitespace-nowrap"
                  >
                    最新爆料
                  </Link>
                  <Link
                    to="/category/meiri-dasai"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-foreground-800 text-foreground-800 text-sm hover:bg-foreground-800/5 transition-colors duration-200 whitespace-nowrap"
                  >
                    热门视频
                  </Link>
                </div>
              </div>
            </div>
          </section>

          <section className="py-10 md:py-14 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {(categoryArticles.length > 0 ? categoryArticles : otherArticles).slice(0, 9).map((article, idx) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-500"
                    style={{ aspectRatio: '3/4' }}
                  >
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading={idx < 3 ? 'eager' : 'lazy'}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
                    {article.hot && (
                      <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-medium flex items-center gap-1 whitespace-nowrap">
                        <i className="ri-fire-line"></i>
                        HOT
                      </div>
                    )}
                    <div className="absolute inset-x-0 bottom-0 p-4 md:p-5">
                      <span className="inline-block px-2.5 py-1 rounded-full bg-accent-500/80 text-background-50 text-xs mb-3">
                        {article.category}
                      </span>
                      <h3 className="text-lg font-heading font-bold text-background-50 mb-2 line-clamp-2 leading-snug">
                        {article.title}
                      </h3>
                      <p className="text-sm text-background-50/70 line-clamp-2 leading-relaxed">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center gap-3 mt-3 text-xs text-background-50/50">
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {article.views}
                        </span>
                        <span>{article.date}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}